import { useCallback, useMemo, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import CrossIcon from './cross-icon.svg'
import {
  applyVoucher,
  refreshCart,
  unapplyVoucher,
  setCouponCode,
  removeCouponCode,
} from '@booking/services/api'
//import { apiApplyVoucher, apiSetCouponCode } from '../../services/bookingApi'

import trackGA4 from '@booking/services/trackingGa4'
import { STORAGE_KEYS } from '@booking/state/booking.storage'
import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { CartLine } from '@booking/types/booking'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'
import { handleAppError } from '../../lib/errors/handleAppError'
import { isAxiosError } from 'axios'
import { ApiError } from '../../lib/errors/apiError'

type DiscountBoxProps = {
  onApplyOrRemove?: () => void | Promise<void>
  hasFired?: boolean
}

export default function DiscountBox({
  onApplyOrRemove = () => {},
  hasFired = false,
}: DiscountBoxProps) {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch, rebuildCart } = useBookingActionsCtx()

  const [unapplying, setUnapplying] = useState(false)
  const [applying, setApplying] = useState(false)
  const [discount, setDiscount] = useState('')
  const [discountError, setDiscountError] = useState(false)

  const cart = state.cart

  const voucherLines = useMemo(
    () => (cart?.lines ?? []).filter((line) => Boolean(line?.voucher)),
    [cart?.lines],
  )

  const refreshCartWrapper = useCallback(
    async (cartId: string) => {
      if (!cartId) return null

      const refreshedCart = await refreshCart(cartId)
      dispatch({ type: 'SET_CART', cart: refreshedCart })

      return refreshedCart
    },
    [dispatch],
  )

  const addVoucher = async (cartId: string, code: string) => {
    try {
      const res = await applyVoucher(cartId, code)
      dispatch({ type: 'SET_CART', cart: res })
      const vouchers = (res?.lines ?? [])
        .filter((line) => Boolean(line?.voucher))
        .map((line) => line.voucher)
      setDiscount('')
      sessionStorage.setItem(STORAGE_KEYS.vouchers, JSON.stringify(vouchers))
    } catch (e) {
      if (e instanceof ApiError && e?.data?.errorCode === 'ENTITY_NOT_FOUND') {
        setDiscount('')
        setDiscountError(true)
      } else {
        handleAppError(e, { dispatch, t })
      }
    }
  }

  const addCoupon = async (cartId: string, code: string) => {
    const normalizedCode = code.toUpperCase()
    setDiscountError(false)

    try {
      const res = await setCouponCode(cartId, normalizedCode)
      dispatch({ type: 'SET_CART', cart: res })
      sessionStorage.setItem(STORAGE_KEYS.discount, normalizedCode)
      trackGA4.addDiscountCode(normalizedCode, true)
    } catch (e) {
      console.log(e)
      if (e instanceof ApiError && e?.data?.errorCode === 'ENTITY_NOT_FOUND') {
        setDiscount('')
        setDiscountError(true)
      } else {
        handleAppError(e, { dispatch, t })
      }
    }
  }

  const addDiscountCode = async () => {
    if (!cart) return
    const input = discount.trim()
    if (!input) return

    setApplying(true)
    let useCart = cart
    if (hasFired) {
      const rebuilt = await rebuildCart()
      if (rebuilt) {
        useCart = rebuilt
      }
    }
    const cartId = useCart.id
    if (!cartId) return

    await onApplyOrRemove()

    if (input.split('-').length === 5) {
      await addVoucher(cartId, input)
    } else {
      await addCoupon(cartId, input)
    }

    setApplying(false)
  }

  const unsetDiscountCode = async () => {
    if (!cart) return

    setUnapplying(true)
    let useCart = cart
    // rebuilding the cart will remove the coupon code
    // TODO: support multiple coupon codes (i.e. rebuild cart with ALL coupons/vouchers)
    if (hasFired) {
      await onApplyOrRemove()
      const rebuilt = await rebuildCart()
      if (rebuilt) {
        useCart = rebuilt
      }
    } else {
      const cartId = useCart.id
      await onApplyOrRemove()
      const res = await removeCouponCode(cartId)
      dispatch({ type: 'SET_CART', cart: res })
    }

    setUnapplying(false)
    sessionStorage.removeItem(STORAGE_KEYS.discount)
  }

  const removeVoucher = async (line: CartLine) => {
    if (!cart) return
    const cartId = cart.id
    const voucherId = line.voucher.id
    if (!cartId || !voucherId) return

    await unapplyVoucher(cartId, voucherId)
    const refreshedCart = await refreshCartWrapper(cartId)
    const vouchers = (refreshedCart?.lines ?? [])
      .filter((nextLine) => Boolean(nextLine?.voucher))
      .map((nextLine) => nextLine.voucher)
    sessionStorage.setItem(STORAGE_KEYS.vouchers, JSON.stringify(vouchers))
  }

  if (!cart) return null

  return (
    <div className={cn(styles.wrapper)}>
      {voucherLines.length > 0 && (
        <div className={styles.codes}>
          {voucherLines.map((line) => (
            <div key={line.voucher.id} className={cn(styles.applied, styles.voucher)}>
              <span>Voucher applied [{line.voucher.id.slice(0, 8)}]</span>
              <button
                className={styles.remove}
                type="button"
                onClick={() => void removeVoucher(line)}
              >
                <CrossIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {cart.discount && (
        <div className={cn(styles.applied, unapplying && styles.blinker)}>
          <span>
            {cart.discount.percentage}% {t('ticket.discount.applied')}
          </span>
          <button
            className={cn(styles.remove)}
            type="button"
            onClick={() => void unsetDiscountCode()}
            disabled={unapplying}
          >
            <CrossIcon />
          </button>
        </div>
      )}

      {!cart.discount && cart.total > 0 && (
        <>
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault()
              void addDiscountCode()
            }}
          >
            <input
              type="text"
              placeholder={t('ticket.discount.code')}
              value={discount}
              onChange={(event) => setDiscount(event.target.value)}
              className={cn(shared.input, styles.input)}
            />
            <button
              className={cn(shared.button, styles.add, applying && styles.blinker)}
              type="submit"
              disabled={applying}
            >
              {!applying ? t('ticket.discount.add') : 'Applying...'}
            </button>
          </form>
          <div className={styles.note}>{t('ticket.discount.note')}</div>
          {discountError ? (
            <div className={styles.discountError}>{t('ticket.discount.invalid')}</div>
          ) : null}
        </>
      )}
    </div>
  )
}
